/**
 * Web port of Coupons/CouponsMapAll.
 *
 * v2 — Google Maps / Uber Eats style layout: a scrollable sidebar of every
 * business (search + category filters + cards) next to the map, replacing
 * the old floating filter panel + bottom sheet. Selecting a business (from
 * the sidebar OR by clicking its pin) expands that business's coupons
 * inline in its card, accordion-style — only one business expanded at a
 * time. On narrow viewports the sidebar collapses into a bottom drawer that
 * peeks over a full-screen map and expands on tap (see .sidebar/.sidebar--*
 * in the stylesheet); selecting a business auto-expands the drawer.
 *
 * Scope agreed with team: markers + info popups, no in-app turn-by-turn
 * directions. "Get Directions" opens Google Maps in a new tab instead of
 * rendering a route polyline on this map (the original used the Routes API +
 * @mapbox/polyline decoding for an in-app route line and step list — that's
 * intentionally dropped here).
 *
 * REDUX: businesses/all and businesses/coupons modules live in
 * coupon-common (same modules the mobile app uses), so this screen dispatches
 * businessActions.getAllList() on mount and couponActions.getBusinessCoupons()
 * when a business is selected, same as mobile's CouponMapAll screen.
 *
 * The `businesses` prop is kept as an optional override — if a parent passes
 * it in, it's used as-is instead of the redux-fetched list. Otherwise the
 * component fetches its own list.
 *
 * Library: @react-google-maps/api. Requires NEXT_PUBLIC_GOOGLE_MAPS_API_KEY /
 * REACT_APP_GOOGLE_MAPS_API_KEY (adjust to your env convention) with the Maps
 * JavaScript API enabled.
 *
 * ALL CITIES: this screen shows businesses across every city regardless of
 * state.cities.activeCityId — see the v1 header comment (removed here to
 * keep this comment block focused) for why that filter was dropped.
 * Businesses without a resolvable `location` still won't render — with
 * DEBUG on below, the console's `missing coords` log lists which business
 * IDs are being dropped for that reason.
 *
 * DROPPED FROM v1: the marker-click InfoWindow "pin badge" (logo + distance
 * bubble) is gone — the sidebar card already shows that info the moment a
 * business is selected, so the extra popup on the map was redundant. Along
 * with it, the :global .gm-style-iw-* overrides that only existed to trim
 * that InfoWindow's chrome are gone too.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { GoogleMap, Marker, OverlayView, useLoadScript } from '@react-google-maps/api';
import {
  ChevronDown,
  ChevronUp,
  Globe,
  Grid,
  MapPin,
  Phone,
  Search as SearchIcon,
  ShoppingBag,
  Sliders,
  Tag,
  User as UserIcon,
  X,
} from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Spinner, SeoHead } from 'components';
import { SITE_URL } from 'components/SeoHead/SeoHead';
import { COUPON_TYPES } from 'coupon-common';
import { actions as businessActions } from 'coupon-common/src/modules/businesses/all';
import { actions as couponActions, duck as businessCouponsDuck } from 'coupon-common/src/modules/businesses/coupons';
import { actions as businessDetailsActions, duck as businessDetailsDuck } from 'coupon-common/src/modules/businesses/details';
import { actions as followActions } from 'coupon-common/src/modules/businesses/follow';

import foodIcon from '../../../assets/icons/food.webp';
import selfCareIcon from '../../../assets/icons/selfcare.webp';
import servicesIcon from '../../../assets/icons/services.webp';
import shoppingIcon from '../../../assets/icons/shopping.webp';
import thingsToDoIcon from '../../../assets/icons/thingstodo.webp';
// Same asset Home.js falls back to for coupons with no photo — path is one
// level deeper here since CouponsMapAll sits one folder further from src/
// than Home does (matches the existing icon imports above). CONFIRM this
// resolves; adjust the leading ../ count if your actual nesting differs.
import NoCouponImage from '../../../assets/images/NoCouponImage.webp';
import LoginPrompt from 'components/LoginPrompt/LoginPrompt';
import { RouteNames } from '../../../constants';
import styles from './CouponsMapAll.module.scss';

// Matches mobile's DEBUG/dbg pattern in CouponMapAll/index.jsx.
const DEBUG = false;
const dbg = (...args) => DEBUG && console.log('[CouponsMapAll]', ...args);

const ICONS = {
  food: foodIcon,
  services: servicesIcon,
  shopping: shoppingIcon,
  thingstodo: thingsToDoIcon,
  selfcare: selfCareIcon,
};

const CATEGORY_FILTERS = [
  { id: 'food', label: 'Food & Drink' },
  { id: 'services', label: 'Services' },
  { id: 'shopping', label: 'Shopping' },
  { id: 'thingstodo', label: 'Things to do' },
  { id: 'selfcare', label: 'Self-care' },
];

// Human-readable label for a business-card's category subtitle (e.g.
// "Shopping" instead of the internal 'shopping' filter key).
const CATEGORY_LABELS = CATEGORY_FILTERS.reduce((acc, cat) => {
  acc[cat.id] = cat.label;
  return acc;
}, {});

// Small 2x2 dot-grid icon for the Category filter — matches the dot-grid
// glyph used for the Category chip on CouponsListAll/Home's hero, rather
// than react-feather's Grid icon (which renders 3x3 squares).
const CategoryDotsIcon = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <circle cx="4.5" cy="4.5" r="1.75" fill="currentColor" />
    <circle cx="11.5" cy="4.5" r="1.75" fill="currentColor" />
    <circle cx="4.5" cy="11.5" r="1.75" fill="currentColor" />
    <circle cx="11.5" cy="11.5" r="1.75" fill="currentColor" />
  </svg>
);

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const GOOGLE_MAPS_LIBRARIES = [];

// Approximate real-world center of Lake Ontario — used to anchor the
// "Lake Ontario" cover badge (see LAKE_LABEL_COVER_STYLE below) to an
// actual lat/lng via OverlayView, instead of a fixed screen %. Google's
// unlicensed/no-billing placeholder tiles reuse real lake geometry under a
// fictional "Lake America" label, so anchoring to the real coordinate
// keeps the badge correctly positioned over the water as the map pans/
// zooms, rather than drifting like a fixed-position overlay would.
const LAKE_ONTARIO_COORDINATE = { lat: 43.71, lng: -78.06 };

// Below this zoom level, Google's placeholder tiles don't render the
// "Lake America" text at a legible size (it's world/continent view at that
// point) — the cover badge only needs to show once zoomed in far enough to
// actually see it, same as the real label would only be legible then too.
const LAKE_LABEL_MIN_ZOOM = 6;

// OverlayView's default anchor is the pixel's top-left corner — this
// shifts the rendered badge up/left by half its own footprint so it's
// centered on LAKE_ONTARIO_COORDINATE instead.
const getLakeLabelPixelOffset = (width, height) => ({ x: -(width / 2), y: -(height / 2) });

const TOP_CAT_TO_ICON_KEY = {
  'food & drink': 'food',
  food: 'food',
  services: 'services',
  shopping: 'shopping',
  'things to do': 'thingstodo',
  'things-to-do': 'thingstodo',
  thingstodo: 'thingstodo',
  'self care': 'selfcare',
  'self-care': 'selfcare',
  selfcare: 'selfcare',
};

function normalizeCatName(str = '') {
  return String(str)
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[^\w &-]/g, '')
    .trim();
}

function getCategoryKeyForBusiness(biz) {
  const firstTopCatName = normalizeCatName(biz?.categories?.[0]?.name || '');
  const key =
    TOP_CAT_TO_ICON_KEY[firstTopCatName] ||
    (firstTopCatName.includes('food')
      ? 'food'
      : firstTopCatName.includes('drink')
        ? 'food'
        : firstTopCatName.includes('service')
          ? 'services'
          : firstTopCatName.includes('shop')
            ? 'shopping'
            : firstTopCatName.includes('thing')
              ? 'thingstodo'
              : firstTopCatName.includes('self')
                ? 'selfcare'
                : null);
  return key || 'services';
}

// TODO: Remove once legacy DB lat/lon swap is corrected. All locations are in
// the northern hemisphere, so a negative latitude means the pair was stored
// in reverse order — swap them back.
const normalizeHemisphere = ({ latitude, longitude }) => {
  if (latitude < 0) {
    return { latitude: longitude, longitude: latitude };
  }
  return { latitude, longitude };
};

// Business photos come back as plain URL strings from the Django API
// (ImageField serializes to an absolute URL) — see business-info saga:
// FORMATTING_FIELDS.LOGO = 'logo', FORMATTING_FIELDS.COVER_IMAGE = 'cover_image'.
const getLogoUrl = biz => (typeof biz?.logo === 'string' && biz.logo ? biz.logo : null);
const getCoverUrl = biz => (typeof biz?.cover_image === 'string' && biz.cover_image ? biz.cover_image : null);

const getInitial = biz => (biz?.name ? biz.name.trim().charAt(0).toUpperCase() : '?');

const extractCoordinate = biz => {
  const loc = biz?.location;
  const coords = loc?.coordinates;
  if (Array.isArray(coords) && coords.length >= 2) {
    const [a, b] = coords;
    const looksLonLat = Math.abs(a) <= 180 && Math.abs(b) <= 90;
    if (looksLonLat) {
      const latitude = b;
      const longitude = a;
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        return normalizeHemisphere({ latitude, longitude });
      }
    } else {
      const latitude = a;
      const longitude = b;
      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        return normalizeHemisphere({ latitude, longitude });
      }
    }
    return null;
  }
  const latitude = Number(biz?.location?.lat ?? biz?.latitude);
  const longitude = Number(biz?.location?.lng ?? biz?.longitude);
  if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
    return normalizeHemisphere({ latitude, longitude });
  }
  return null;
};

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Same logic as Home.js's FeaturedFavouriteCardBase, so a coupon shows the
// identical "$5 off" / "20% off" / "150 points" subtitle on both screens.
const getCouponDealAmount = coupon => {
  if (coupon?.type === COUPON_TYPES.AMOUNT_OFF) return `$${coupon.amount_off} off`;
  if (coupon?.type === COUPON_TYPES.PERCENTAGE_OFF) return `${Math.round(coupon.percentage_off)}% off`;
  if (coupon?.type === COUPON_TYPES.POINTS_BACK) return `${coupon.points_back} points`;
  return null;
};

const getCouponImage = coupon => {
  const images = coupon?.image_url;
  return Array.isArray(images) && images.length > 0 ? images[0].image_url : NoCouponImage;
};

const BUSINESS_COUPONS_INITIAL_STATE = businessCouponsDuck.BUSINESS_COUPONS_INITIAL_STATE;
const BUSINESS_DETAIL_INITIAL_STATE = businessDetailsDuck.BUSINESS_DETAIL_INITIAL_STATE;

const MAP_STYLE = [
  { featureType: 'poi', stylers: [{ visibility: 'on' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'labels', stylers: [{ visibility: 'simplified' }] },
  { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', stylers: [{ visibility: 'simplified' }] },
];

/**
 * Filter-bar dropdown — a single trigger button that opens a checkbox list,
 * used for both Category and City. Empty `selectedIds` means "no filter
 * applied" (everything shown), not "nothing selected" — checking an option
 * narrows the list down to just the checked ones. Closes on outside click,
 * same pattern as Home.js's HeroDropdownPortal (not portaled here since
 * this bar isn't near any clipped/scrolling ancestor).
 */
const FilterDropdown = ({ label, icon, options, selectedIds, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const triggerLabel = selectedIds.length === 0 ? label : `${label} (${selectedIds.length})`;

  return (
    <div className={styles['filter-dropdown']} ref={wrapRef}>
      <button
        type="button"
        className={
          selectedIds.length > 0
            ? `${styles['filter-dropdown-trigger']} ${styles['filter-dropdown-trigger-active']}`
            : styles['filter-dropdown-trigger']
        }
        onClick={() => setOpen(o => !o)}
      >
        {icon}
        <span className={styles['filter-dropdown-trigger-text']}>{triggerLabel}</span>
        <ChevronDown size={15} className={open ? styles['filter-chevron-open'] : undefined} />
      </button>

      {open && (
        <div className={styles['filter-dropdown-menu']}>
          {options.length === 0 && <p className={styles['filter-dropdown-empty']}>Nothing to filter by yet.</p>}
          {options.map(opt => (
            <label key={opt.id} className={styles['filter-dropdown-option']}>
              <input type="checkbox" checked={selectedIds.includes(opt.id)} onChange={() => onToggle(opt.id)} />
              <span>{opt.label}</span>
            </label>
          ))}
          {selectedIds.length > 0 && (
            <button type="button" className={styles['filter-dropdown-clear']} onClick={onClear}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Single-select sort dropdown — same trigger/menu chrome as FilterDropdown
 * (pill trigger + portal-less menu), but picking an option replaces the
 * current value and closes the menu immediately instead of toggling a
 * checkbox list. Used for "Nearest First" / "Name (A-Z)" so it no longer
 * falls back to a native <select>'s unstyled OS dropdown.
 */
const SortDropdown = ({ icon, options, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = options.find(opt => opt.id === value);

  return (
    <div className={styles['filter-dropdown']} ref={wrapRef}>
      <button type="button" className={styles['filter-dropdown-trigger']} onClick={() => setOpen(o => !o)}>
        {icon}
        <span className={styles['filter-dropdown-trigger-text']}>{selected ? selected.label : 'Sort'}</span>
        <ChevronDown size={15} className={open ? styles['filter-chevron-open'] : undefined} />
      </button>

      {open && (
        <div className={styles['filter-dropdown-menu']}>
          {options.map(opt => (
            <button
              key={opt.id}
              type="button"
              className={
                opt.id === value
                  ? `${styles['filter-dropdown-option']} ${styles['filter-dropdown-option-selected']}`
                  : styles['filter-dropdown-option']
              }
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
            >
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Category filter dropdown — single-select, styled exactly like
 * CouponsListAll's hero Category dropdown: a full-width gradient
 * "All categories" pill on its own row, then a 2-col grid of pill options
 * (icon + label) below, with the active one highlighted via `.active`.
 * Replaces the generic checkbox-list FilterDropdown just for Category so
 * this filter bar's category picker matches CouponsListAll's exactly.
 */
const CategoryDropdown = ({ options, icons, selectedId, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selected = options.find(opt => opt.id === selectedId);

  return (
    <div className={styles['filter-dropdown']} ref={wrapRef}>
      <button
        type="button"
        className={
          selectedId
            ? `${styles['filter-dropdown-trigger']} ${styles['filter-dropdown-trigger-active']}`
            : styles['filter-dropdown-trigger']
        }
        onClick={() => setOpen(o => !o)}
      >
        <CategoryDotsIcon size={15} />
        <span className={styles['filter-dropdown-trigger-text']}>{selected ? selected.label : 'Category'}</span>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>

      {open && (
        <div className={styles['category-dropdown-menu']}>
          <button
            type="button"
            className={
              !selectedId
                ? `${styles['category-dropdown-item']} ${styles['category-dropdown-item-all']} ${styles['category-dropdown-item-active']}`
                : `${styles['category-dropdown-item']} ${styles['category-dropdown-item-all']}`
            }
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            <Grid size={15} />
            <span>All categories</span>
          </button>
          {options.map(opt => (
            <button
              key={opt.id}
              type="button"
              className={
                opt.id === selectedId
                  ? `${styles['category-dropdown-item']} ${styles['category-dropdown-item-active']}`
                  : styles['category-dropdown-item']
              }
              onClick={() => {
                onSelect(opt.id);
                setOpen(false);
              }}
            >
              {icons[opt.id] && <img src={icons[opt.id]} alt="" className={styles['category-dropdown-item-icon']} />}
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * @param {Object[]} [businesses] - optional business list override. If
 *   omitted, the component fetches its own list via
 *   businessActions.getAllList() (coupon-common/businesses/all).
 */
const CouponsMapAll = ({ businesses: businessesProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const externalDest = location.state?.dest || null;

  // Mirrors mobile's mapStateToProps: businessAllState / businessCouponsState.
  const businessAllState = useSelector(state => state.businesses.all);

  const { fetching: bizFetching, fetched: bizFetched, data: bizData } = businessAllState || {};
  const { results: fetchedBusinesses = [] } = bizData || {};

  // `businesses` prop is an optional override; otherwise use the
  // redux-fetched list, same data source as mobile.
  const businesses = businessesProp ?? fetchedBusinesses;

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Built only once the script has actually finished loading, so
  // window.google.maps is guaranteed to be populated. Building these inline
  // in JSX (window.google?.maps?.SymbolPath?.CIRCLE) can race the script load
  // and hand the Marker an icon with path: undefined, which throws inside
  // the Marker component.
  const userLocationIcon = useMemo(() => {
    if (!isLoaded || !window.google) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 7,
      fillColor: '#2563eb',
      fillOpacity: 1,
      strokeColor: '#fff',
      strokeWeight: 2,
    };
  }, [isLoaded]);

  const markerIconSize = useMemo(() => {
    if (!isLoaded || !window.google) return undefined;
    return new window.google.maps.Size(30, 30);
  }, [isLoaded]);

  const selectedMarkerIconSize = useMemo(() => {
    if (!isLoaded || !window.google) return undefined;
    return new window.google.maps.Size(38, 38);
  }, [isLoaded]);

  // Soft red halo rendered behind the selected marker — bumping the icon
  // size alone (30px → 38px) turned out too subtle to notice at a glance,
  // especially with several pins clustered together. The halo gives it a
  // much bigger visual footprint without the actual pin icon itself
  // getting cartoonishly large.
  const selectedHaloIcon = useMemo(() => {
    if (!isLoaded || !window.google) return undefined;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      scale: 22,
      fillColor: '#e53935',
      fillOpacity: 0.2,
      strokeColor: '#e53935',
      strokeOpacity: 0.55,
      strokeWeight: 2,
    };
  }, [isLoaded]);

  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // `state.businesses.coupons` is a map keyed by business id (mirrors
  // BusinessesDetails.js) — NOT a single flat {fetching, data} record. Must
  // be indexed by the selected business's id, or couponsData is always
  // whatever happens to sit at the duck's root instead of this business's
  // coupons.
  const businessCouponsAllState = useSelector(state => state.businesses.coupons);
  const businessCouponsState = (selectedId && businessCouponsAllState?.[selectedId]) || BUSINESS_COUPONS_INITIAL_STATE;
  // fetched/error are added here (this file previously only read fetching/
  // data) so the fetch effect below can tell "already have this business's
  // coupons cached" apart from "never fetched yet" — see that effect for
  // the caching logic itself. ASSUMPTION: businessCouponsDuck's per-business
  // entries follow the same {fetching, fetched, data, error} shape every
  // other duck in this codebase uses (confirmed for businesses.all just
  // above via bizFetched, customerCoupons.all, customerCoupons.categorized,
  // etc.) — if businesses/coupons's duck.js names this field differently,
  // the dbg log added below the fetch effect will print the real shape.
  const { fetching: couponsFetching, fetched: couponsFetched, data: couponsData, error: couponsError } = businessCouponsState;
  // API response shape is `{ coupons: [...] }` — `.results`/bare-array kept
  // as fallbacks in case other callers of this duck get a differently
  // shaped response.
  const businessCoupons = Array.isArray(couponsData)
    ? couponsData
    : couponsData?.coupons || couponsData?.results || [];

  // Full business detail (cover_image, phone, website_url, street — none of
  // which the businesses/all list this sidebar is built from reliably
  // includes) for the redesigned business-info header, keyed by id same as
  // businessCouponsAllState above. Same duck/action CouponsDetails.js and
  // BusinessesDetails.js already use for this data.
  const businessDetailsAllState = useSelector(state => state.businesses.details);
  const businessDetailState = (selectedId && businessDetailsAllState?.[selectedId]) || BUSINESS_DETAIL_INITIAL_STATE;
  const { data: businessDetailData } = businessDetailState;

  // ─── Follow state — same pattern as BusinessesDetails.js's Business Info
  // card, reused here for the identical card in the expanded sidebar entry.
  const authorized = useSelector(state => !!(state.auth && state.auth.authorized));
  const followState = useSelector(state => state.businesses.follow);
  const followRecord = useMemo(
    () => (followState?.data?.results || []).find(b => b.business_id === selectedId),
    [followState?.data?.results, selectedId],
  );
  const isBusinessFollowed = !!followRecord;
  const [loginPromptVisible, setLoginPromptVisible] = useState(false);

  useEffect(() => {
    dispatch(followActions.getFollowedBusinessesRequest());
  }, [dispatch]);

  const toggleFollowHandler = useCallback(() => {
    if (!authorized) {
      setLoginPromptVisible(true);
      return;
    }
    if (!selectedId) return;
    // Looked up from `businesses` directly (not selectedEntry, which is
    // declared further down this component) so this handler can sit next
    // to the rest of the follow state above.
    const isAffiliateOrFlyer = !!businesses.find(b => b.id === selectedId)?.is_affiliate_or_flyer;
    if (isBusinessFollowed) {
      dispatch(
        followActions.unfollowBusinessRequest({
          id: followRecord?.id,
          business_id: selectedId,
          is_affiliate_or_flyer: isAffiliateOrFlyer,
        }),
      );
    } else {
      dispatch(
        followActions.followBusinessRequest({
          business_id: selectedId,
          is_affiliate_or_flyer: isAffiliateOrFlyer,
        }),
      );
    }
  }, [authorized, dispatch, selectedId, isBusinessFollowed, followRecord, businesses]);

  const [searchQuery, setSearchQuery] = useState('');
  // Empty array = no filter applied (show every category/city) — checking
  // an option narrows down to just what's checked. Replaces the old
  // always-all-true chip-row state now that Category is one dropdown
  // filter instead of five separate toggle buttons.
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedCityIds, setSelectedCityIds] = useState([]);
  // Only meaningful once at least one category is selected — see
  // subcategoryOptions below, which mirrors CouponsListAll's "subcategory
  // menu only appears once dynamicSubcategories is non-empty" gating.
  const [selectedSubcategoryIds, setSelectedSubcategoryIds] = useState([]);
  // Off by default — matches the existing behavior of hiding businesses
  // with no active coupons (see visibleEntries). Checking this box shows
  // every business regardless of coupon count.
  const [showBusinessesWithoutCoupons, setShowBusinessesWithoutCoupons] = useState(false);
  const [sortBy, setSortBy] = useState('distance'); // 'distance' | 'name'
  // Mobile bottom-drawer state — irrelevant above the mobile breakpoint,
  // where the sidebar is always fully visible (see stylesheet).
  const [drawerExpanded, setDrawerExpanded] = useState(false);

  const cardRefs = useRef({});

  // ─── Fetch business list (skip if a businesses prop override was passed) ──
  useEffect(() => {
    if (businessesProp) {
      dbg('businesses prop override passed in, skipping getAllList dispatch. count:', businessesProp.length);
      return;
    }
    dbg('dispatching businessActions.getAllList()');
    dispatch(businessActions.getAllList());
  }, [dispatch, businessesProp]);

  useEffect(() => {
    dbg(
      'businessAllState changed — fetching:', bizFetching,
      'fetched:', bizFetched,
      'results count:', fetchedBusinesses.length,
      'error:', businessAllState?.error,
    );
  }, [bizFetching, bizFetched, fetchedBusinesses.length, businessAllState?.error]);

  // ─── Browser geolocation ────────────────────────────────────────────────
  const requestUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not available in this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        setLocationError(null);
        setUserLocation({ latitude: position.coords.latitude, longitude: position.coords.longitude });
      },
      err => setLocationError(err.message || 'Could not determine your location.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  }, []);

  useEffect(() => {
    requestUserLocation();
  }, [requestUserLocation]);

  const toggleCityFilter = useCallback(id => {
    setSelectedCityIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);
  const clearCityFilter = useCallback(() => setSelectedCityIds([]), []);

  // Same manual-selection guard as CouponsListAll's SUBCATEGORY MAPPING
  // effect below: toggling a subcategory sets this before updating state,
  // so the reset effect (which fires whenever selectedCategoryIds changes)
  // can tell "category selection actually changed" apart from "re-ran for
  // some unrelated reason right after the user picked a subcategory" and
  // skip clearing what they just picked.
  const manuallySelectedSubcategories = useRef(false);
  const toggleSubcategoryFilter = useCallback(id => {
    manuallySelectedSubcategories.current = true;
    setSelectedSubcategoryIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  }, []);
  const clearSubcategoryFilter = useCallback(() => setSelectedSubcategoryIds([]), []);

  // Built from the raw (unfiltered) business list so the City dropdown
  // always offers every city with at least one business, regardless of
  // whatever the other filters currently hide. ASSUMPTION: business.city
  // is { id, name, ... } per BusinessListSerializer's CitySerializer field
  // — confirm those two keys exist if city options come back empty.
  const cityOptions = useMemo(() => {
    const seen = new Map();
    businesses.forEach(biz => {
      const city = biz.city;
      if (city?.id != null && city?.name && !seen.has(city.id)) {
        seen.set(city.id, { id: city.id, label: city.name });
      }
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [businesses]);

  const categoryOptions = useMemo(() => CATEGORY_FILTERS.map(cat => ({ id: cat.id, label: cat.label })), []);

  // Mirrors CouponsListAll's SUBCATEGORY MAPPING logic, adapted for
  // businesses instead of coupons and for our multi-select Category filter
  // instead of their single-select one: options are the union of
  // `business.subcategories` across every business belonging to any
  // currently-selected category, so the dropdown never offers a
  // subcategory with zero matching businesses. Empty when no category is
  // selected — that's what gates the dropdown from rendering at all below
  // (same as their `dynamicSubcategories.length > 0` check), satisfying
  // "once at least one category is enabled, enable the subcategory
  // filter." ASSUMPTION: business.subcategories is an array of
  // { id, name } objects, same shape as categories/city — confirm if this
  // comes back empty despite businesses genuinely having subcategories.
  const subcategoryOptions = useMemo(() => {
    if (selectedCategoryIds.length === 0) return [];
    const seen = new Map();
    businesses.forEach(biz => {
      if (!selectedCategoryIds.includes(getCategoryKeyForBusiness(biz))) return;
      (biz.subcategories || []).forEach(sub => {
        if (sub?.id != null && sub?.name && !seen.has(sub.id)) {
          seen.set(sub.id, { id: sub.id, label: sub.name });
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [businesses, selectedCategoryIds]);

  // Clears the subcategory selection whenever the category selection
  // itself changes — same behavior as CouponsListAll (a subcategory
  // belonging to a category you just deselected shouldn't silently keep
  // filtering). Skipped once, right after a manual subcategory toggle, via
  // the ref set in toggleSubcategoryFilter above.
  useEffect(() => {
    if (manuallySelectedSubcategories.current) {
      manuallySelectedSubcategories.current = false;
      return;
    }
    setSelectedSubcategoryIds([]);
  }, [selectedCategoryIds]);

  // Intentionally NOT filtered by city — see file header. The map/sidebar
  // show every business with a resolvable location, regardless of whichever
  // city is active elsewhere in the app.
  const businessesWithCoords = useMemo(
    () =>
      businesses
        .map(business => {
          const coord = extractCoordinate(business);
          if (!coord) return null;
          return {
            business,
            coordinate: coord,
            isSponsored: !!business.is_advertised,
            categoryKey: getCategoryKeyForBusiness(business),
            // From BusinessListSerializer.active_coupon_count (annotated in
            // AllBusinessList.get_queryset — see backend). Coerced defensively:
            // Number(undefined) is NaN, not 0, so `|| 0` catches both a
            // missing field (backend not deployed yet) and a genuinely-empty
            // one, without silently treating a real 0 as missing.
            couponCount: Number(business.active_coupon_count) || 0,
          };
        })
        .filter(Boolean),
    [businesses],
  );

  useEffect(() => {
    dbg('businesses total:', businesses.length);
    dbg('businesses with coords:', businessesWithCoords.length);
    dbg(
      'missing coords:',
      businesses.filter(biz => !extractCoordinate(biz)).map(biz => biz.id),
    );
    // If this logs a count > 0, the API isn't sending active_coupon_count
    // yet (or is sending it under a different key) — worth checking before
    // assuming the coupon-count filter below is actually doing anything.
    const missingCouponCount = businesses.filter(biz => biz.active_coupon_count === undefined).length;
    if (missingCouponCount > 0) {
      dbg('businesses missing active_coupon_count field:', missingCouponCount, 'of', businesses.length);
    }
  }, [businesses, businessesWithCoords]);

  // Adds distanceKm (when userLocation is known) so the sidebar can sort by
  // proximity the way Maps/Uber Eats-style lists do.
  const entriesWithDistance = useMemo(
    () =>
      businessesWithCoords.map(entry => ({
        ...entry,
        distanceKm: userLocation
          ? calculateDistanceKm(userLocation.latitude, userLocation.longitude, entry.coordinate.latitude, entry.coordinate.longitude)
          : null,
      })),
    [businessesWithCoords, userLocation],
  );

  // Single source of truth for both the sidebar list AND the map pins, so
  // they never disagree — same filters, same search query, same sort.
  const visibleEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return entriesWithDistance
      .filter(entry => showBusinessesWithoutCoupons || entry.couponCount > 0)
      .filter(entry => selectedCategoryIds.length === 0 || selectedCategoryIds.includes(entry.categoryKey))
      .filter(
        entry =>
          selectedSubcategoryIds.length === 0 ||
          (entry.business.subcategories || []).some(sub => selectedSubcategoryIds.includes(sub.id)),
      )
      .filter(entry => selectedCityIds.length === 0 || selectedCityIds.includes(entry.business?.city?.id))
      .filter(entry => {
        if (!q) return true;
        const name = entry.business?.name?.toLowerCase() || '';
        const street = entry.business?.street?.toLowerCase() || '';
        return name.includes(q) || street.includes(q);
      })
      .sort((a, b) => {
        if (sortBy === 'name') {
          return (a.business?.name || '').localeCompare(b.business?.name || '');
        }
        // 'distance' — entries without a known distance (no userLocation
        // yet) sort after ones that have it, instead of comparing as 0.
        if (a.distanceKm == null && b.distanceKm == null) return 0;
        if (a.distanceKm == null) return 1;
        if (b.distanceKm == null) return -1;
        return a.distanceKm - b.distanceKm;
      });
  }, [
    entriesWithDistance,
    selectedCategoryIds,
    selectedSubcategoryIds,
    selectedCityIds,
    searchQuery,
    showBusinessesWithoutCoupons,
    sortBy,
  ]);

  const goToBusinessDetails = businessId => {
    navigate(`${RouteNames.BUSINESSES_DETAILS}/${businessId}`);
  };

  // Same route Home.js's goToDetails uses for its coupon cards
  // (`${RouteNames.COUPONS_DETAILS}/${coupon.id}`) — kept as its own
  // function rather than inlining the navigate() call so it reads the
  // same way at the call site as goToBusinessDetails above.
  const goToCouponDetails = couponId => {
    navigate(`${RouteNames.COUPONS_DETAILS}/${couponId}`);
  };

  // Shared by sidebar-card clicks and marker clicks — accordion behavior:
  // clicking the already-selected business collapses it again.
  const selectBusiness = useCallback(entry => {
    const id = entry?.business?.id;
    if (!id) return;
    setSelectedId(prev => (prev === id ? null : id));
    setDrawerExpanded(true);
    dbg('selected business:', id, entry.business?.name);
  }, []);

  const handleMarkerClick = entry => {
    selectBusiness(entry);
  };

  const handleGetDirections = coordinate => {
    if (!coordinate) return;
    const { latitude, longitude } = coordinate;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Same handlers BusinessesDetails.js uses for its hero icon row (Website/
  // Call/Directions) — reused here for the redesigned business-info header.
  const handleVisitWebsite = url => {
    if (!url) return;
    const withScheme = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    window.open(withScheme, '_blank', 'noopener,noreferrer');
  };

  const openPhoneNumber = phone => {
    if (phone) window.location.href = `tel:${phone}`;
  };

  const selectedEntry = useMemo(
    () => visibleEntries.find(entry => entry.business.id === selectedId) || null,
    [visibleEntries, selectedId],
  );

  // Scrolls the sidebar list (not the whole page) so the selected card
  // sits near the top — runs for both marker clicks and direct sidebar
  // clicks, since both go through selectBusiness/selectedId. Previously
  // this only fired from marker clicks (via a one-off scrollIntoView in
  // handleMarkerClick), so clicking a card directly did nothing, and a
  // card near the bottom would expand its coupons off the visible list
  // with no way to see them without a manual scroll.
  //
  // Uses getBoundingClientRect() math instead of scrollIntoView: this
  // effect runs after the DOM has already committed the newly-expanded
  // (or newly-collapsed) card, so the offsets below account for its real
  // height, and scrolling businessListRef directly (rather than letting
  // the browser walk up the ancestor chain) keeps this contained to the
  // sidebar's own scroll — it can't also drag the outer page/map with it.
  const businessListRef = useRef(null);
  useEffect(() => {
    if (!selectedId) return;
    const container = businessListRef.current;
    const card = cardRefs.current[selectedId];
    if (!container || !card) return;
    const cardRect = card.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const relativeTop = cardRect.top - containerRect.top + container.scrollTop;
    container.scrollTo({ top: Math.max(relativeTop - 8, 0), behavior: 'smooth' });
  }, [selectedId]);

  // Pans the map to center on whichever business is selected — imperative
  // (map.panTo via the instance ref), not by changing the `center` prop.
  // `center` below is deliberately only driven by userLocation (see its
  // own comment on why), so re-purposing it here would either fight that
  // memoization or reintroduce the "map snaps back on every click" bug it
  // was written to fix. panTo animates smoothly, which setCenter doesn't.
  //
  // Depends on the coordinate's raw lat/lng, not `selectedEntry` itself —
  // selectedEntry is a new object every time visibleEntries recomputes
  // (e.g. the search box changes while the same business stays selected),
  // which would re-trigger the pan for no reason if it were a dependency.
  const mapRef = useRef(null);
  const onMapLoad = useCallback(map => {
    mapRef.current = map;
  }, []);

  // Tracks the map's live zoom level so the "Lake Ontario" cover badge
  // (see LAKE_ONTARIO_COORDINATE / LAKE_LABEL_MIN_ZOOM above) only renders
  // once the user has zoomed in close enough to actually see Google's
  // placeholder "Lake America" text — at a world/continent view the badge
  // would otherwise cover an enormous, nonsensical area (see screenshot
  // feedback: it was swallowing half of North America at zoom ~3).
  const [mapZoom, setMapZoom] = useState(13);
  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) setMapZoom(mapRef.current.getZoom());
  }, []);

  useEffect(() => {
    if (!selectedEntry || !mapRef.current) return;
    mapRef.current.panTo({ lat: selectedEntry.coordinate.latitude, lng: selectedEntry.coordinate.longitude });
  }, [selectedEntry?.coordinate.latitude, selectedEntry?.coordinate.longitude]);

  // Fetch coupons for the selected business — mirrors mobile's
  // useEffect(() => { dispatch(couponActions.getBusinessCoupons(id)) }, [selected, dispatch])
  //
  // CACHING: state.businesses.coupons[id] persists in redux for as long as
  // this browser session lasts — redux isn't torn down when this screen
  // unmounts on route navigation, only on a hard refresh. So once a
  // business's coupons have been fetched once, they're already "cached"
  // for free; the only thing missing was a guard to stop re-fetching them
  // every single time that business gets re-selected (re-expanding a card
  // you already opened earlier, or navigating away from this screen and
  // back). couponsFetched skips a redundant re-fetch; couponsError still
  // allows a retry on reselect rather than getting stuck showing a
  // permanent error from an earlier attempt.
  useEffect(() => {
    if (!selectedId) return;

    if (couponsFetched && !couponsError) {
      dbg('coupons already cached for business id:', selectedId, '— skipping fetch');
      return;
    }
    if (couponsFetching) {
      dbg('coupons already in flight for business id:', selectedId, '— skipping duplicate dispatch');
      return;
    }

    dbg('selected business changed, dispatching couponActions.getBusinessCoupons for id:', selectedId);
    dispatch(couponActions.getBusinessCoupons(selectedId));
  }, [selectedId, dispatch, couponsFetched, couponsFetching, couponsError]);

  // Same caching guard as the coupons fetch above, for the full business
  // detail (cover photo, phone, website) the redesigned info header needs.
  useEffect(() => {
    if (!selectedId) return;
    if (businessDetailState.fetched && !businessDetailState.error) return;
    if (businessDetailState.fetching) return;
    dispatch(businessDetailsActions.getDetail(selectedId));
  }, [selectedId, dispatch, businessDetailState.fetched, businessDetailState.fetching, businessDetailState.error]);

  useEffect(() => {
    if (!selectedId) return;
    dbg(
      'businessCouponsState changed — fetching:', couponsFetching,
      'fetched:', couponsFetched,
      'coupons count:', businessCoupons.length,
      'error:', couponsError,
      'raw state:', businessCouponsState,
    );
  }, [couponsFetching, couponsFetched, businessCoupons.length, couponsError, selectedId, businessCouponsState]);

  // ─── External destination (e.g. navigated here from CouponsDetails "Directions") ──
  useEffect(() => {
    if (externalDest) {
      handleGetDirections({ latitude: externalDest.latitude, longitude: externalDest.longitude });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDest]);

  // IMPORTANT: @react-google-maps/api re-applies controlled props (center,
  // options) whenever their reference changes, not just their values. An
  // inline object literal here is a *new* reference every render, so any
  // state change elsewhere in this component (e.g. selecting a business) was
  // causing map.setCenter() to fire again and snap the map back to
  // userLocation/Halifax — this was the "map moves a lot on every click" bug.
  // Memoizing on the actual lat/lng values fixes it.
  const center = useMemo(
    () =>
      userLocation
        ? { lat: userLocation.latitude, lng: userLocation.longitude }
        : { lat: 44.6488, lng: -63.5752 }, // fallback: Halifax, NS
    [userLocation?.latitude, userLocation?.longitude],
  );

  const mapOptions = useMemo(() => ({ styles: MAP_STYLE, disableDefaultUI: false, clickableIcons: false }), []);

  // Pins .page's height to "however much viewport is actually left below
  // wherever this element starts", instead of assuming a CSS percentage/vh
  // chain that turned out not to hold (this page sits below the app's
  // persistent header, and a plain `height: 100%`/`100vh` on .page doesn't
  // know how tall that header is — hence the page-level vertical scroll).
  // getBoundingClientRect().top gives the real on-screen offset regardless
  // of what's above it or how that ancestor chain is styled, so this works
  // without any assumption about the surrounding layout.
  const pageRef = useRef(null);
  const updatePageHeight = useCallback(() => {
    if (!pageRef.current) return;
    const top = pageRef.current.getBoundingClientRect().top;
    pageRef.current.style.height = `${Math.max(window.innerHeight - top, 0)}px`;
  }, []);

  // Re-measures whenever the flags below flip — those are exactly the
  // conditions that swap which early-return branch renders (loadError /
  // !isLoaded / businesses-still-fetching / the real map view), each with
  // its OWN <div className={styles.page}> element. pageRef.current ends up
  // pointing at a different DOM node each time that happens, and a
  // mount-only effect wouldn't know to re-measure the new one — this
  // dependency list is what catches that. Layout effect (not a plain
  // effect) so there's no flash of the old, too-tall height on any of
  // these transitions.
  useLayoutEffect(() => {
    updatePageHeight();
  }, [updatePageHeight, loadError, isLoaded, businessesProp, bizFetching, bizFetched]);

  // Separately: window resize, and 'load' for late layout shifts from web
  // fonts/images finishing after the initial measurement (e.g. the header
  // growing a line once its font swaps in). Registered once — these fire
  // regardless of which branch is currently mounted.
  useEffect(() => {
    window.addEventListener('resize', updatePageHeight);
    window.addEventListener('load', updatePageHeight);
    return () => {
      window.removeEventListener('resize', updatePageHeight);
      window.removeEventListener('load', updatePageHeight);
    };
  }, [updatePageHeight]);

  if (loadError) {
    return (
      <div className={styles['page']} ref={pageRef}>
        <div className={styles['center-state']}>
          <p className={styles['center-state-text']}>Failed to load Google Maps. Check your API key configuration.</p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={styles['page']} ref={pageRef}>
        <div className={styles['center-state']}>
          <Spinner show size="lg" />
          <p className={styles['center-state-text']}>Loading map…</p>
        </div>
      </div>
    );
  }

  // Mirrors mobile's `notReady` check (bizFetching && !bizFetched) — only
  // applies when we're sourcing the list from redux; a parent-supplied
  // businesses prop has nothing to wait on.
  if (!businessesProp && bizFetching && !bizFetched) {
    return (
      <div className={styles['page']} ref={pageRef}>
        <div className={styles['center-state']}>
          <Spinner show size="lg" />
          <p className={styles['center-state-text']}>Loading businesses…</p>
        </div>
      </div>
    );
  }


  // ─── SEO ────────────────────────────────────────────────────────────
// NOTE: same caveat as CouponsListAll.js — filters live in local state,
// not the URL, so `path`/canonical is always the same generic route
// regardless of what's filtered. This page is also almost entirely
// client-rendered (map + JS-driven list), so treat this as improving the
// tab title / share preview rather than actual search-engine indexing.


const activeCategoryLabels = categoryOptions
  .filter(opt => selectedCategoryIds.includes(opt.id))
  .map(opt => opt.label);
const activeCityLabels = cityOptions.filter(opt => selectedCityIds.includes(opt.id)).map(opt => opt.label);

const brandSuffix = ' | My Couponbook';
const cityText = activeCityLabels.join(', ');

const seoTitle = searchQuery.trim()
  ? `Map results for "${searchQuery.trim()}"${brandSuffix}`
  : `Find Local Businesses${cityText ? ` in ${cityText}` : ' Near You'}${brandSuffix}`;

const seoDescription = `Explore our interactive directory to find the best local businesses near you${
  cityText ? ` in ${cityText}` : ''
}. See what stores, restaurants, and services are in your neighborhood.`;

// const seoTitle = searchQuery.trim()
//   ? `Map results for "${searchQuery.trim()}"`
//   : activeCategoryLabels.length > 0
//   ? `${activeCategoryLabels.join(', ')} Businesses${activeCityLabels.length > 0 ? ` in ${activeCityLabels.join(', ')}` : ''} — Map`
//   : activeCityLabels.length > 0
//   ? `Businesses Near ${activeCityLabels.join(', ')} — Map`
//   : 'Businesses Near You — Map';
// const seoDescription = `Find local businesses and deals on the map${
//   activeCategoryLabels.length > 0 ? ` in ${activeCategoryLabels.join(', ')}` : ''
// }${activeCityLabels.length > 0 ? ` near ${activeCityLabels.join(', ')}` : ''} on My Couponbook.`;
const structuredData =
  visibleEntries.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: visibleEntries.slice(0, 50).map((entry, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: entry.business.name,
          url: `${SITE_URL}${RouteNames.BUSINESSES_DETAILS}/${entry.business.id}`,
        })),
      }
    : undefined;

  return (
    <div className={styles['page']} ref={pageRef}>
      <SeoHead
       title={seoTitle}
       description={seoDescription}
       path={RouteNames.COUPONS_MAPS_ALL}
       type="website"
       structuredData={structuredData}
     />

      {/* ─── Filter bar — sits above both the sidebar and the map, same role
          as Turo's filter row above its list/map split. ─── */}
      <div className={styles['filter-bar']}>
        <FilterDropdown
          label="Select Location"
          icon={<MapPin size={15} />}
          options={cityOptions}
          selectedIds={selectedCityIds}
          onToggle={toggleCityFilter}
          onClear={clearCityFilter}
        />

        <CategoryDropdown
          options={categoryOptions}
          icons={ICONS}
          selectedId={selectedCategoryIds[0] || null}
          onSelect={id => setSelectedCategoryIds(id ? [id] : [])}
        />

        {/* Only rendered once a category is selected AND that category has
            subcategories among the current businesses — mirrors
            CouponsListAll's `{dynamicSubcategories.length > 0 && (...)}`. */}
        {subcategoryOptions.length > 0 && (
          <FilterDropdown
            label="Subcategory"
            options={subcategoryOptions}
            selectedIds={selectedSubcategoryIds}
            onToggle={toggleSubcategoryFilter}
            onClear={clearSubcategoryFilter}
          />
        )}

        <SortDropdown
          icon={<Sliders size={15} />}
          value={sortBy}
          onChange={setSortBy}
          options={[
            { id: 'distance', label: 'Nearest First' },
            { id: 'name', label: 'Name (A–Z)' },
          ]}
        />

        <button
          type="button"
          className={
            showBusinessesWithoutCoupons
              ? `${styles['toggle-pill']} ${styles['toggle-pill-active']}`
              : styles['toggle-pill']
          }
          onClick={() => setShowBusinessesWithoutCoupons(v => !v)}
        >
          <Tag size={15} />
          <span>Show Businesses Without Coupons</span>
        </button>
      </div>

      <div className={styles['layout']}>
        <aside className={`${styles['sidebar']} ${drawerExpanded ? styles['sidebar-expanded'] : ''}`}>
          <button
            type="button"
            className={styles['drawer-handle']}
            onClick={() => setDrawerExpanded(open => !open)}
            aria-label={drawerExpanded ? 'Collapse list' : 'Expand list'}
          >
            <span className={styles['drawer-handle-bar']} />
          </button>

          <div className={styles['sidebar-header']}>
            <div className={styles['sidebar-search-row']}>
              <SearchIcon size={16} className={styles['search-icon']} aria-hidden="true" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search For Coupons."
                className={styles['search-input']}
              />
              {searchQuery && (
                <button
                  type="button"
                  className={styles['search-clear']}
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className={styles['sidebar-header-top']}>
              <div>
                <h1 className={styles['sidebar-title']}>Nearby Deals</h1>
                <p className={styles['results-count']}>
                  {visibleEntries.length} {visibleEntries.length === 1 ? 'Business' : 'Businesses'}
                </p>
              </div>
              {!userLocation && (
                <button type="button" className={styles['enable-location-link']} onClick={requestUserLocation}>
                  Enable Your Location For A Better Experience
                </button>
              )}
            </div>
          </div>

          <div className={styles['business-list']} ref={businessListRef}>
            {locationError && <p className={styles['sidebar-subtle-error']}>{locationError}</p>}

            {visibleEntries.length === 0 && (
              <p className={styles['empty-list-text']}>No businesses match your filters.</p>
            )}

            {visibleEntries.map(entry => {
              const { business, isSponsored, categoryKey, couponCount } = entry;
              const isSelected = business.id === selectedId;
              // Full detail (cover photo/phone/website) is only fetched for
              // whichever business is currently selected/expanded — see the
              // businessDetailState effect above. Falls back to whatever the
              // businesses/all list entry itself already carries so nothing
              // flashes blank while that fetch is in flight.
              const heroCoverUrl = isSelected ? getCoverUrl(businessDetailData) || getCoverUrl(business) : null;
              const heroWebsiteUrl = isSelected ? businessDetailData?.website_url || business.website_url : null;
              const heroPhone = isSelected ? businessDetailData?.phone || business.phone : null;
              // Business Info card fields — same fallback pattern as the
              // hero fields above (businessDetailData once fetched, else
              // whatever the businesses/all list entry already carries).
              const heroOwnerName = isSelected ? businessDetailData?.owner_name || business.owner_name : null;
              const heroStreet = isSelected ? businessDetailData?.street || business.street : null;
              const heroCity = isSelected ? businessDetailData?.city || business.city : null;
              const heroPostalCode = isSelected ? businessDetailData?.postal_code || business.postal_code : null;
              const heroAddress1 = heroStreet || '';
              const heroAddress2 = `${heroCity?.name || ''}${heroPostalCode ? `, ${heroPostalCode}` : ''}`;
              return (
                <div
                  key={business.id}
                  ref={el => (cardRefs.current[business.id] = el)}
                  className={
                    isSelected
                      ? `${styles['business-card']} ${styles['business-card-selected']}`
                      : styles['business-card']
                  }
                >
                  {!isSelected ? (
                    <button
                      type="button"
                      className={styles['business-card-summary']}
                      onClick={() => selectBusiness(entry)}
                    >
                      <div className={styles['business-avatar-wrap']}>
                        {getLogoUrl(business) ? (
                          <img src={getLogoUrl(business)} alt="" className={styles['business-avatar']} />
                        ) : (
                          <div className={styles['business-avatar-fallback']}>{getInitial(business)}</div>
                        )}
                      </div>

                      <div className={styles['business-summary-text']}>
                        <div className={styles['business-name-row']}>
                          {isSponsored && <span className={styles['sponsored-pill']}>Premium</span>}
                          <p className={styles['business-name']}>{business.name}</p>
                        </div>
                        <p className={styles['business-meta']}>{CATEGORY_LABELS[categoryKey] || categoryKey}</p>
                      </div>

                      <div className={styles['business-card-side']}>
                        <span className={styles['coupon-count-pill']}>
                          {couponCount} {couponCount === 1 ? 'Offer' : 'Offers'}
                        </span>
                      </div>
                    </button>
                  ) : (
                    // Plain div (not <button>) — the frosted-glass quick-action
                    // icons below now sit ON the photo itself (matching
                    // BusinessesDetails.js's heroIconsRow), and nesting real
                    // <button> elements inside a <button> is invalid HTML.
                    // role/tabIndex/onKeyDown keep this clickable/keyboard
                    // accessible the way the old <button> was.
                    <div
                      role="button"
                      tabIndex={0}
                      className={styles['business-hero']}
                      onClick={() => selectBusiness(entry)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') selectBusiness(entry);
                      }}
                    >
                      <div className={styles['business-hero-image-wrap']}>
                        {heroCoverUrl ? (
                          <img src={heroCoverUrl} alt="" className={styles['business-hero-image']} />
                        ) : (
                          <div className={styles['business-hero-image-fallback']} />
                        )}
                        <div className={styles['business-hero-overlay']} />

                        {/* Offer-count badge — takes over the top-right spot
                            on the photo that the quick-action icons used to
                            occupy (those moved below the category line). */}
                        <span className={`${styles['coupon-count-pill']} ${styles['business-hero-offer-badge']}`}>
                          {couponCount} {couponCount === 1 ? 'Offer' : 'Offers'}
                        </span>
                      </div>

                      {/* Logo overlaps the image/white-card boundary (its
                          bottom half hangs below the image). Name/category
                          sit in their own band that stays entirely within
                          the image's darkened area — deliberately NOT
                          vertically centered against the logo's full
                          height, which would push the category line onto
                          the plain white card body below the image (white/
                          light-gray text there is invisible). */}
                      <div className={styles['business-hero-logo']}>
                        {getLogoUrl(business) ? (
                          <img src={getLogoUrl(business)} alt="" />
                        ) : (
                          <div className={styles['business-hero-logo-fallback']}>{getInitial(business)}</div>
                        )}
                      </div>

                      <div className={styles['business-hero-text']}>
                        {isSponsored && <span className={styles['sponsored-pill']}>Premium</span>}
                        <p className={styles['business-hero-name']}>{business.name}</p>
                        <p className={styles['business-hero-category']}>{CATEGORY_LABELS[categoryKey] || categoryKey}</p>

                        {/* Quick-action icons — same frosted-glass pill
                            treatment as BusinessesDetails.js's
                            .heroIconsRow, now sitting under the category
                            line instead of the photo's top-right corner. */}
                        <div className={styles['business-hero-icons-overlay']}>
                          {heroWebsiteUrl && (
                            <button
                              type="button"
                              className={styles['hero-icon-btn']}
                              onClick={e => {
                                e.stopPropagation();
                                handleVisitWebsite(heroWebsiteUrl);
                              }}
                              title="Website"
                            >
                              <Globe size={16} />
                            </button>
                          )}
                          {heroPhone && (
                            <button
                              type="button"
                              className={styles['hero-icon-btn']}
                              onClick={e => {
                                e.stopPropagation();
                                openPhoneNumber(heroPhone);
                              }}
                              title="Call"
                            >
                              <Phone size={15} />
                            </button>
                          )}
                          <button
                            type="button"
                            className={styles['hero-icon-btn']}
                            onClick={e => {
                              e.stopPropagation();
                              handleGetDirections(entry.coordinate);
                            }}
                            title="Directions"
                          >
                            <MapPin size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {isSelected && (
                    <div className={styles['business-card-details']}>
                      <div className={styles['actions-row']}>
                        <button
                          type="button"
                          className={styles['primary-btn']}
                          onClick={() => handleGetDirections(entry.coordinate)}
                        >
                          <MapPin size={15} />
                          Get Direction
                        </button>
                        <button
                          type="button"
                          className={styles['secondary-btn']}
                          onClick={() => goToBusinessDetails(business.id)}
                        >
                          <UserIcon size={15} />
                          View Profile
                        </button>
                      </div>

                      {/* Business Info card — same layout as
                          BusinessesDetails.js's own Business Info card,
                          reused here so the expanded map sidebar entry
                          shows the identical name/owner/phone/address rows
                          + Follow button. */}
                      <div className={styles['info-card']}>
                        <div className={styles['info-card-header']}>
                          <ShoppingBag size={18} className={styles['info-card-header-icon']} />
                          <h3 className={styles['info-card-header-title']}>Business Info</h3>
                        </div>

                        <div className={styles['info-list']}>
                          {business.name && (
                            <div className={styles['info-row']}>
                              <span className={styles['info-icon']}>
                                <ShoppingBag size={16} />
                              </span>
                              <span className={styles['info-value']}>{business.name}</span>
                            </div>
                          )}
                          {heroOwnerName && (
                            <div className={styles['info-row']}>
                              <span className={styles['info-icon']}>
                                <UserIcon size={16} />
                              </span>
                              <span className={styles['info-value']}>{heroOwnerName}</span>
                            </div>
                          )}
                          {heroPhone && (
                            <div className={styles['info-row']}>
                              <span className={styles['info-icon']}>
                                <Phone size={16} />
                              </span>
                              <span className={styles['info-value']}>{heroPhone}</span>
                            </div>
                          )}
                          {(heroAddress1 || heroAddress2) && (
                            <div className={styles['info-row']}>
                              <span className={styles['info-icon']}>
                                <MapPin size={16} />
                              </span>
                              <span className={styles['info-value']}>
                                {heroAddress1}
                                {heroAddress1 && heroAddress2 ? ', ' : ''}
                                {heroAddress2}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className={styles['info-follow-wrap']}>
                          <button
                            type="button"
                            className={
                              isBusinessFollowed
                                ? styles['info-follow-btn']
                                : `${styles['info-follow-btn']} ${styles['info-follow-btn-inactive']}`
                            }
                            onClick={toggleFollowHandler}
                          >
                            {isBusinessFollowed ? 'Following' : 'Follow'}
                          </button>
                        </div>
                      </div>

                      <div className={styles['coupons-section']}>
                        <h3 className={styles['coupons-header']}>Available Coupons</h3>
                        {couponsFetching && <Spinner show size="sm" />}
                        {!couponsFetching && businessCoupons.length === 0 && (
                          <p className={styles['coupon-empty-text']}>No active coupons found for this business.</p>
                        )}
                        {!couponsFetching && businessCoupons.length > 0 && (
                          <div className={styles['coupons-grid']}>
                            {businessCoupons.map(coupon => (
                              <button
                                key={coupon.id}
                                type="button"
                                className={styles['coupon-photo-card']}
                                onClick={() => goToCouponDetails(coupon.id)}
                              >
                                <img src={getCouponImage(coupon)} alt="" className={styles['coupon-photo-card-img']} />
                                <div className={styles['coupon-photo-card-overlay']} />
                                <span className={styles['coupon-photo-card-tag']}>
                                  {CATEGORY_LABELS[categoryKey] || 'Store'}
                                </span>
                                <div className={styles['coupon-photo-card-text']}>
                                  <p className={styles['coupon-photo-card-title']}>
                                    {getCouponDealAmount(coupon) || coupon.title || 'Special Offer'}
                                  </p>
                                  <p className={styles['coupon-photo-card-subtitle']}>Store offer</p>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <div className={styles['map-container']}>
          <GoogleMap
            center={center}
            zoom={13}
            options={mapOptions}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            onClick={() => setSelectedId(null)}
            onZoomChanged={handleZoomChanged}
            onLoad={onMapLoad}
          >
            {userLocation && (
              <Marker
                position={{ lat: userLocation.latitude, lng: userLocation.longitude }}
                icon={userLocationIcon}
              />
            )}

            {selectedEntry && (
              <Marker
                position={{ lat: selectedEntry.coordinate.latitude, lng: selectedEntry.coordinate.longitude }}
                icon={selectedHaloIcon}
                zIndex={998}
                clickable={false}
              />
            )}

            {visibleEntries.map(entry => {
              const { business, coordinate, categoryKey } = entry;
              const isSelected = business.id === selectedId;
              return (
                <Marker
                  key={business.id}
                  position={{ lat: coordinate.latitude, lng: coordinate.longitude }}
                  icon={{
                    url: ICONS[categoryKey],
                    scaledSize: isSelected ? selectedMarkerIconSize : markerIconSize,
                  }}
                  // Dims every other pin while one is selected, so the
                  // halo + size bump aren't fighting a busy map for
                  // attention — especially useful with pins clustered
                  // close together.
                  opacity={selectedId && !isSelected ? 0.55 : 1}
                  zIndex={isSelected ? 999 : undefined}
                  onClick={() => handleMarkerClick(entry)}
                />
              );
            })}

            {/* Covers Google's "Lake America" placeholder label — shows up
                instead of real water-body names whenever the Maps JS API
                key is missing/invalid or billing isn't enabled on it
                (Google serves fictional geography so an unlicensed map is
                obviously a demo, not real data). Anchored to
                LAKE_ONTARIO_COORDINATE via OverlayView (not a fixed
                screen %), so it tracks pan/zoom the same way a real map
                label would — this is still a visual patch, not a real fix;
                once a valid, billed GOOGLE_MAPS_API_KEY is wired up, real
                tiles (with the actual label, exactly positioned) will
                render and this cover can be removed. Only rendered once
                zoomed in past LAKE_LABEL_MIN_ZOOM — at a world/continent
                view the badge would otherwise cover a huge, nonsensical
                chunk of the map instead of just the lake. */}
            {mapZoom >= LAKE_LABEL_MIN_ZOOM && (
              <OverlayView position={LAKE_ONTARIO_COORDINATE} mapPaneName={OverlayView.FLOAT_PANE} getPixelPositionOffset={getLakeLabelPixelOffset}>
                <div className={styles['lake-label-cover']} aria-hidden="true">
                  Lake Ontario
                </div>
              </OverlayView>
            )}
          </GoogleMap>

          <button
            type="button"
            className={styles['locate-btn']}
            onClick={requestUserLocation}
            aria-label="Recenter on my location"
          >
            ⌖
          </button>
        </div>
      </div>

      <LoginPrompt
        isOpen={loginPromptVisible}
        onClose={() => setLoginPromptVisible(false)}
      />
    </div>
  );
};

export default CouponsMapAll;